/**
 * Safe public labels for servers/providers.
 * NEVER expose wholesale vendor brands in UI labels.
 */

export type ServerCatalogId =
  | 'proxy_cheap'
  | 'proxyline'
  | 'proxylte'
  | 'proxy5g'
  | 'proxygb'
  | 'smspool'
  | 'durianRcs'
  | 'textverified_temp'
  | 'pvadeals6'
  | 'textverified_perm'
  | 'textverified_voice';

/** Neutral defaults (Arabic) — match Backend serverCatalog.defaultNameAr */
export const SAFE_SERVER_LABELS_AR: Record<ServerCatalogId, string> = {
  proxy_cheap: 'بروكسي 1',
  proxyline: 'بروكسي 2',
  proxylte: 'بروكسي 3',
  proxy5g: 'بروكسي 4',
  proxygb: 'بروكسي 5',
  smspool: 'سيرفر 1',
  durianRcs: 'سيرفر 2',
  textverified_temp: 'سيرفر 3',
  pvadeals6: 'سيرفر 4',
  textverified_perm: 'أرقام دائمة',
  textverified_voice: 'اتصال صوتي',
};

export const SAFE_SERVER_LABELS_EN: Record<ServerCatalogId, string> = {
  proxy_cheap: 'Proxy Server 1',
  proxyline: 'Proxy Server 2',
  proxylte: 'Proxy Server 3',
  proxy5g: 'Proxy Server 4',
  proxygb: 'Proxy Server 5',
  smspool: 'Temp Server 1',
  durianRcs: 'Temp Server 2',
  textverified_temp: 'Temp Server 3',
  pvadeals6: 'Temp Server 4',
  textverified_perm: 'Permanent Numbers',
  textverified_voice: 'Voice Call',
};

/** Map profit-margin / cashback API keys → catalog id */
export const SERVICE_KEY_TO_CATALOG: Record<string, ServerCatalogId> = {
  smspool: 'smspool',
  pvadeals: 'pvadeals6',
  pvadeals6: 'pvadeals6',
  durianRcs: 'durianRcs',
  proxylte: 'proxylte',
  proxyline: 'proxyline',
  proxycheap: 'proxy_cheap',
  proxy_cheap: 'proxy_cheap',
  textverified: 'textverified_temp',
  textverified_temp: 'textverified_temp',
  textverified_perm: 'textverified_perm',
  textverified_voice: 'textverified_voice',
  proxy5g: 'proxy5g',
  proxygb: 'proxygb',
};

/** User-margins numeric serverId → catalog id */
export const MARGIN_SERVER_TO_CATALOG: Record<string, Record<number, ServerCatalogId>> = {
  temporaryNumbers: { 1: 'smspool', 4: 'durianRcs', 5: 'textverified_temp', 6: 'pvadeals6' },
  temporaryNumbersReuse: { 5: 'textverified_temp', 6: 'pvadeals6' },
  voice: { 1: 'textverified_voice' },
  permanentNumbers: { 1: 'textverified_perm' },
  proxies: { 1: 'proxy_cheap', 2: 'proxyline', 3: 'proxylte', 4: 'proxy5g', 5: 'proxygb' },
};

export const DASHBOARD_SERVER_GROUPS: {
  label: string;
  servers: { id: ServerCatalogId }[];
}[] = [
  {
    label: 'بروكسيات',
    servers: [
      { id: 'proxy_cheap' },
      { id: 'proxyline' },
      { id: 'proxylte' },
      { id: 'proxy5g' },
      { id: 'proxygb' },
    ],
  },
  {
    label: 'أرقام مؤقتة',
    servers: [
      { id: 'smspool' },
      { id: 'durianRcs' },
      { id: 'textverified_temp' },
      { id: 'pvadeals6' },
    ],
  },
  {
    label: 'أرقام دائمة',
    servers: [{ id: 'textverified_perm' }],
  },
];

export type ServerLabelRow = {
  id: string;
  defaultNameAr?: string;
  displayName?: string;
};

/** Resolve a safe label from server-labels API rows + fallbacks */
export function resolveServerLabel(
  catalogId: string,
  labelRows?: ServerLabelRow[] | null,
  lang: 'ar' | 'en' = 'ar'
): string {
  const row = labelRows?.find((s) => s.id === catalogId);
  const custom = row?.displayName?.trim();
  if (custom) return custom;
  if (row?.defaultNameAr?.trim()) return row.defaultNameAr.trim();
  const id = catalogId as ServerCatalogId;
  const map = lang === 'en' ? SAFE_SERVER_LABELS_EN : SAFE_SERVER_LABELS_AR;
  return map[id] || catalogId;
}

export function resolveServiceKeyLabel(
  serviceKey: string,
  labelRows?: ServerLabelRow[] | null,
  lang: 'ar' | 'en' = 'ar'
): string {
  if (serviceKey === 'warehouse') {
    return lang === 'en' ? 'Warehouse' : 'المستودع';
  }
  const catalogId = SERVICE_KEY_TO_CATALOG[serviceKey] || serviceKey;
  return resolveServerLabel(catalogId, labelRows, lang);
}

export function resolveMarginServerLabel(
  marginType: string,
  serverId: number,
  labelRows?: ServerLabelRow[] | null,
  lang: 'ar' | 'en' = 'ar'
): string {
  const catalogId = MARGIN_SERVER_TO_CATALOG[marginType]?.[serverId];
  if (!catalogId) return `${lang === 'en' ? 'Server' : 'سيرفر'} ${serverId}`;
  return resolveServerLabel(catalogId, labelRows, lang);
}

/** Flatten server-labels API response into one list */
export function flattenServerLabelGroups(data?: {
  proxies?: ServerLabelRow[];
  tempNumbers?: ServerLabelRow[];
  permNumbers?: ServerLabelRow[];
} | null): ServerLabelRow[] {
  if (!data) return [];
  return [...(data.proxies || []), ...(data.tempNumbers || []), ...(data.permNumbers || [])];
}

/** Map provider filter values to safe labels for numbers admin */
export function resolveProviderFilterLabel(provider: string, labelRows?: ServerLabelRow[] | null): string {
  const raw = String(provider || '').trim();
  if (!raw) return '-';

  const normalized = raw.toLowerCase().replace(/[\s_-]/g, '');
  const brandToKey: Record<string, string> = {
    textverified: 'textverified',
    pvadeals: 'pvadeals',
    pvadeals6: 'pvadeals',
    smspool: 'smspool',
    durianrcs: 'durianRcs',
    durian: 'durianRcs',
  };
  const mappedKey = brandToKey[normalized] || raw;
  const map: Record<string, ServerCatalogId> = {
    textverified: 'textverified_temp',
    pvadeals: 'pvadeals6',
    smspool: 'smspool',
    durianRcs: 'durianRcs',
  };
  const catalogId = map[mappedKey] || SERVICE_KEY_TO_CATALOG[mappedKey];
  if (!catalogId) {
    // Last resort: never echo wholesale brands
    if (/proxycheap|smspool|textverified|durian|proxyline|proxylte|pvadeals/i.test(raw)) {
      return 'سيرفر';
    }
    return raw;
  }
  return resolveServerLabel(catalogId, labelRows);
}

/** Scrub accidental wholesale brand names from merchant-facing strings (notes, etc.) */
export function scrubProviderBrands(text: string | null | undefined): string {
  if (text == null) return '';
  let out = String(text);
  const reps: [RegExp, string][] = [
    [new RegExp('Proxy' + 'Cheap', 'gi'), 'بروكسي 1'],
    [new RegExp('Proxy' + '-Cheap', 'gi'), 'بروكسي 1'],
    [new RegExp('Proxy' + 'Line', 'gi'), 'بروكسي 2'],
    [new RegExp('Proxy' + 'LTE', 'gi'), 'بروكسي 3'],
    [new RegExp('Proxy' + '5G', 'gi'), 'بروكسي 4'],
    [new RegExp('SMS' + 'Pool\\.?' + 'net', 'gi'), 'سيرفر 1'],
    [new RegExp('SMS' + 'Pool', 'gi'), 'سيرفر 1'],
    [new RegExp('Durian' + 'RCS', 'gi'), 'سيرفر 2'],
    [new RegExp('Text' + 'Verified', 'gi'), 'سيرفر 3'],
    [new RegExp('PVA\\s*' + 'Deals', 'gi'), 'سيرفر 4'],
    [new RegExp('PVA' + 'deals', 'gi'), 'سيرفر 4'],
    [new RegExp('Tell\\s*A?\\s*Bot', 'gi'), 'سيرفر'],
    [/panel\.proxyline\.net/gi, 'بروكسي 2'],
    [/api\.durianrcs\.com/gi, 'سيرفر 2'],
    [/smspool\.net/gi, 'سيرفر 1'],
    [/textverified\.com/gi, 'سيرفر 3'],
    [/proxy-?cheap\.com/gi, 'بروكسي 1'],
    [/ofdevs?\.com/gi, 'بروكسي 3'],
    [/\bDurian\s*:/gi, 'سيرفر 2:'],
    [/\bProxy5G\s*:/gi, 'بروكسي 4:'],
    [/\bProxyLTE\s*:/gi, 'بروكسي 3:'],
    [/\bProxyLine\s*:/gi, 'بروكسي 2:'],
    [/\bProxyCheap\s*:/gi, 'بروكسي 1:'],
    [/\bSMSPool\s*:/gi, 'سيرفر 1:'],
    [/\bTextVerified\s*:/gi, 'سيرفر 3:'],
    [/\bPVADeals\s*:/gi, 'سيرفر 4:'],
    [/\btellabot\b/gi, 'سيرفر'],
    [/\bdurian\b/gi, 'سيرفر 2'],
    [/\bofdevs?\b/gi, 'بروكسي 3'],
    [/بروكسي\s*رخيص/gi, 'بروكسي 1'],
    [/بروكسي\s*خط/gi, 'بروكسي 2'],
    [/بروكسي\s*LTE/gi, 'بروكسي 3'],
    [/\bsmspooltemporarynumbers\b/gi, 'سيرفر 1'],
    [/\bduriantemporarynumbers\b/gi, 'سيرفر 2'],
    [/\bpermanentnumbers(_temp)?\b/gi, 'أرقام دائمة'],
    [/\bsmspool\.?net\b/gi, 'سيرفر 1'],
    [/\bsmspool\b/gi, 'سيرفر 1'],
    [/\bdurianrcs\b/gi, 'سيرفر 2'],
    [/\btextverified(_temp|_perm|_voice|-rental)?\b/gi, 'سيرفر 3'],
    [/\btextverified-rental\b/gi, 'أرقام دائمة'],
    [/\bpvadeals6?\b/gi, 'سيرفر 4'],
    [/\bproxy[_-]?cheap\b/gi, 'بروكسي 1'],
    [/\bproxyline\b/gi, 'بروكسي 2'],
    [/\bproxylte\b/gi, 'بروكسي 3'],
    [/\bproxy5g\b/gi, 'بروكسي 4'],
    [/\bMaster\s+provider\b/gi, 'المزوّد'],
    [/\bvia\s+Master\b/gi, ''],
    [/\bMaster\s+API\b/gi, 'الخدمة'],
    [/\bwholesale\b/gi, ''],
    [/\btenantOrderId\b/gi, 'رقم الطلب'],
  ];
  for (const [re, replacement] of reps) out = out.replace(re, replacement);
  return out.replace(/[ \t]{2,}/g, ' ').trim();
}

/** Map raw provider / collection id → safe merchant label */
export function maskProviderLabel(raw: string | null | undefined, lang: 'ar' | 'en' = 'ar'): string {
  if (raw == null || String(raw).trim() === '') return '—';
  const s = String(raw).trim();
  const key = s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const ar: Record<string, string> = {
    smspool: 'سيرفر 1',
    smspoolnet: 'سيرفر 1',
    smspooltemporarynumbers: 'سيرفر 1',
    durianrcs: 'سيرفر 2',
    duriantemporarynumbers: 'سيرفر 2',
    textverified: 'سيرفر 3',
    textverifiedtemp: 'سيرفر 3',
    textverifiedperm: 'أرقام دائمة',
    textverifiedvoice: 'اتصال صوتي',
    permanentnumbers: 'أرقام دائمة',
    permanentnumberstemp: 'سيرفر 3',
    pvadeals: 'سيرفر 4',
    pvadeals6: 'سيرفر 4',
    proxycheap: 'بروكسي 1',
    proxy_cheap: 'بروكسي 1',
    proxyline: 'بروكسي 2',
    proxylte: 'بروكسي 3',
    proxy5g: 'بروكسي 4',
  proxygb: 'بروكسي 5',
  };
  const en: Record<string, string> = {
    smspool: 'Temp Server 1',
    smspoolnet: 'Temp Server 1',
    smspooltemporarynumbers: 'Temp Server 1',
    durianrcs: 'Temp Server 2',
    duriantemporarynumbers: 'Temp Server 2',
    textverified: 'Temp Server 3',
    textverifiedtemp: 'Temp Server 3',
    textverifiedperm: 'Permanent Numbers',
    textverifiedvoice: 'Voice Call',
    permanentnumbers: 'Permanent Numbers',
    permanentnumberstemp: 'Temp Server 3',
    pvadeals: 'Temp Server 4',
    pvadeals6: 'Temp Server 4',
    proxycheap: 'Proxy Server 1',
    proxy_cheap: 'Proxy Server 1',
    proxyline: 'Proxy Server 2',
    proxylte: 'Proxy Server 3',
    proxy5g: 'Proxy Server 4',
  proxygb: 'Proxy Server 5',
  };
  const map = lang === 'en' ? en : ar;
  if (map[key]) return map[key];
  const catalogId = SERVICE_KEY_TO_CATALOG[s] || SERVICE_KEY_TO_CATALOG[key];
  if (catalogId) return resolveServerLabel(catalogId, null, lang);
  return scrubProviderBrands(s) || '—';
}
